import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import type { RootState } from '../../redux/store';
import { TaskySpotlight } from '../tasky';
import tutorialService, { type TutorialSeenAction, type TutorialStatus } from '../../services/api/tutorialService';
import { findTutorialPage } from './registry';
import type { TutorialPageDef, TutorialStep } from './types';

/**
 * 画面の描画を待つ間隔。対象が1つ見つかってから SETTLE_MS 待っても残りが出なければ、
 * 出ていない対象のステップは飛ばす（権限・データ次第で出ない要素がある）。
 * 1つも見つからないまま LOAD_WAIT_MS 経ったら今回は出さない。
 */
const POLL_INTERVAL_MS = 300;
const SETTLE_MS = 1500;
const LOAD_WAIT_MS = 8000;
/** 他のモーダル（お知らせ等）が閉じるのを待つ上限。超えたら今回は出さない */
const DIALOG_WAIT_MS = 60000;

/** API が落ちていても同じ端末で出し直さないための控え */
const localSeenKey = (userId: string | number) => `tasky:tutorial-seen:${userId}`;

const readLocalSeen = (userId: string | number): string[] => {
  try {
    const raw = window.localStorage.getItem(localSeenKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    return [];
  }
};

const writeLocalSeen = (userId: string | number, keys: string[]) => {
  try {
    window.localStorage.setItem(localSeenKey(userId), JSON.stringify(keys));
  } catch {
    // 保存できない環境ではサーバー記録だけに頼る
  }
};

const findTarget = (target: string): Element | null => {
  // PC用・スマホ用で同じ名前を付けた要素があるので、見えている最初の1つを使う。
  // display:none や閉じたメニューの中身は強調できないので無いものとして扱う
  const candidates = Array.from(document.querySelectorAll(`[data-tutorial="${CSS.escape(target)}"]`));
  return (
    candidates.find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }) ?? null
  );
};

/** チュートリアル以外のモーダルが開いているか（お知らせ・確認ダイアログの上に重ねない） */
const isOtherDialogOpen = (): boolean =>
  Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"], [aria-modal="true"]')).some(
    (element) => !element.closest('[data-tutorial-root]'),
  );

interface ResolvedStep {
  step: TutorialStep;
  element: Element | null;
}

interface ActiveTour {
  page: TutorialPageDef;
  steps: ResolvedStep[];
  index: number;
}

/**
 * 現在のページにチュートリアル定義があり、管理画面でONかつ未読なら、
 * 画面の描画を待ってからスポットライトを出す。スキップ/完了で既読にして二度と出さない。
 */
const TutorialHost: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const userId = user?.id ?? null;
  const isEndUser = Boolean(isAuthenticated && userId !== null && user?.role !== 'admin');

  const [status, setStatus] = useState<TutorialStatus | null>(null);
  const [tour, setTour] = useState<ActiveTour | null>(null);
  const seenRef = useRef<Set<string>>(new Set());

  // ログインユーザーが変わったら取り直す
  useEffect(() => {
    setStatus(null);
    setTour(null);
    if (!isEndUser || userId === null) return undefined;

    let cancelled = false;
    seenRef.current = new Set(readLocalSeen(userId));
    tutorialService
      .getStatus()
      .then((next) => {
        if (cancelled) return;
        next.seen_pages.forEach((key) => seenRef.current.add(key));
        setStatus(next);
      })
      .catch(() => {
        // 取れないときは出さない（チュートリアルが無くても操作はできる）
      });
    return () => {
      cancelled = true;
    };
  }, [isEndUser, userId]);

  const page = useMemo(() => findTutorialPage(location.pathname), [location.pathname]);

  const eligiblePage = useMemo(() => {
    if (!status || !status.enabled || !page) return null;
    if (status.disabled_pages.includes(page.key)) return null;
    if (seenRef.current.has(page.key)) return null;
    return page;
  }, [status, page]);

  // ページが変わったら描画を待って開始する。途中で離れたら既読にせず閉じる
  useEffect(() => {
    setTour(null);
    if (!eligiblePage) return undefined;

    const startedAt = Date.now();
    let firstFoundAt: number | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;

      if (isOtherDialogOpen()) {
        if (elapsed < DIALOG_WAIT_MS) timer = setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }

      const resolved = eligiblePage.steps.map((step) => ({
        step,
        element: step.target ? findTarget(step.target) : null,
      }));
      const anyFound = resolved.some((entry) => entry.element);
      const missing = resolved.some((entry) => entry.step.target && !entry.element);
      if (anyFound && firstFoundAt === null) firstFoundAt = Date.now();

      if (!anyFound) {
        // 強調できる要素が1つも無いページ（読み込み失敗・権限で空）は、今回は出さず既読にもしない
        if (elapsed < LOAD_WAIT_MS) timer = setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }
      if (missing && firstFoundAt !== null && Date.now() - firstFoundAt < SETTLE_MS) {
        timer = setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }

      const available = resolved.filter((entry) => !entry.step.target || entry.element);
      setTour({ page: eligiblePage, steps: available, index: 0 });
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [eligiblePage]);

  const finish = useCallback(
    (action: TutorialSeenAction) => {
      if (!tour || userId === null) return;
      const key = tour.page.key;
      seenRef.current.add(key);
      writeLocalSeen(userId, Array.from(seenRef.current));
      setTour(null);
      tutorialService.markSeen(key, action).catch(() => {
        // 端末側の控えがあるので同じ端末では再表示しない。次回ログイン時に別端末で出る可能性だけ残る
      });
    },
    [tour, userId],
  );

  const handleNext = useCallback(() => {
    if (!tour) return;
    if (tour.index >= tour.steps.length - 1) {
      finish('completed');
      return;
    }
    // 次のステップの対象が消えていたら（画面が変わった）その場で探し直す
    const nextIndex = tour.index + 1;
    const nextStep = tour.steps[nextIndex];
    const refreshed = nextStep.step.target ? findTarget(nextStep.step.target) ?? nextStep.element : null;
    const steps = tour.steps.slice();
    steps[nextIndex] = { ...nextStep, element: refreshed };
    setTour({ ...tour, steps, index: nextIndex });
  }, [tour, finish]);

  const handleSkip = useCallback(() => finish('skipped'), [finish]);

  if (!tour) return null;
  const current = tour.steps[tour.index];
  const element = current.element && current.element.isConnected ? current.element : null;

  return (
    <TaskySpotlight
      target={element}
      title={current.step.title}
      body={current.step.body}
      stepIndex={tour.index}
      stepCount={tour.steps.length}
      onNext={handleNext}
      onSkip={handleSkip}
    />
  );
};

export default TutorialHost;
